import {
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@material-ui/core'
import { FlexHorizontal, Spacer } from '@vestaboard/installables'
import { MessageSetSortBy, MessageSetSortDirection } from '../types'
import React, { useState } from 'react'
import { SortDirection, SortableTableCell } from '../../../ui/SortableTableCell'

import { Alert } from '@material-ui/lab'
import { Check } from '@material-ui/icons'
import InfiniteScroll from 'react-infinite-scroller'
import { SearchInput } from '../../../ui/SearchInput'
import { TruncatedId } from '../../truncated-id'
import { useHistory } from 'react-router'
import { useListMessageSets } from '../hooks/useListMessageSets'

export const ListMessageSets = () => {
  const [deviceType, setDeviceType] = useState<string>('all')
  const [search, setSearch] = useState<string | null>(null)
  const [sortDirection, setSortDirection] = useState<MessageSetSortDirection>(MessageSetSortDirection.asc)
  const [sortBy, setSortBy] = useState<MessageSetSortBy>(MessageSetSortBy.title)
  const history = useHistory()
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const { data, loading, error, fetchMore } = useListMessageSets({
    input: {
      cursor: null,
      limit: 50,
      sortDirection,
      sortBy,
      title: search || undefined,
      isNote: deviceType === 'all' ? undefined : deviceType === 'note' ? true : false
    }
  })

  return (
    <Box>
      <FlexHorizontal spaceBetween>
        <Typography variant='h4'>Message Sets</Typography>
        <FlexHorizontal>
          <Select value={deviceType} onChange={e => setDeviceType(e.target.value as string)} variant='outlined'>
            <MenuItem value='all'>All</MenuItem>
            <MenuItem value='note'>Vestaboard Note</MenuItem>
            <MenuItem value='flagship'>Flagship</MenuItem>
          </Select>
          <Box width={20} />
          <SearchInput onSearch={setSearch} size='medium' />
          <Box width={20} />
          <Button onClick={() => history.push('/message-sets/create')} variant='contained' color='primary'>
            Create New Message Set
          </Button>
        </FlexHorizontal>
      </FlexHorizontal>
      <Spacer size='large' />
      {loading && !data?.listMessageSets ? (
        <CircularProgress />
      ) : error ? (
        <Alert severity='error'>There was an error loading the message sets</Alert>
      ) : !data?.listMessageSets.messageSets.length ? (
        <Alert severity='info'>There are no message sets</Alert>
      ) : (
        <>
          <InfiniteScroll
            pageStart={0}
            loadMore={async () => {
              if (isLoadingMore) {
                return
              }

              setIsLoadingMore(true)
              return await fetchMore({
                updateQuery(previousQueryResult, options) {
                  const newMessageSets = options.fetchMoreResult?.listMessageSets.messageSets || []
                  const previousMessageSets = previousQueryResult.listMessageSets.messageSets || []

                  return {
                    listMessageSets: {
                      ...previousQueryResult.listMessageSets,
                      nextCursor: options.fetchMoreResult?.listMessageSets.nextCursor || null,
                      messageSets: [...previousMessageSets, ...newMessageSets]
                    }
                  }
                },
                variables: {
                  input: {
                    sortDirection,
                    sortBy,
                    title: search || undefined,
                    cursor: data.listMessageSets.nextCursor || null,
                    limit: 50,
                    isNote: deviceType === 'all' ? undefined : deviceType === 'note' ? true : false
                  }
                }
              }).then(res => {
                setIsLoadingMore(false)
                return res
              })
            }}
            hasMore={!!data?.listMessageSets?.nextCursor}
            loader={
              <Box>
                <Spacer size='large' />
                <CircularProgress />
              </Box>
            }>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell
                      style={{
                        width: 100
                      }}>
                      ID
                    </TableCell>
                    <SortableTableCell
                      active={sortBy === MessageSetSortBy.title}
                      direction={SortDirection[sortDirection]}
                      onClick={direction => {
                        setSortDirection(MessageSetSortDirection[direction])
                        setSortBy(MessageSetSortBy.title)
                      }}>
                      Title
                    </SortableTableCell>
                    <TableCell
                      style={{
                        width: 120
                      }}>
                      Display Type
                    </TableCell>
                    <SortableTableCell
                      width={100}
                      active={sortBy === MessageSetSortBy.public}
                      direction={SortDirection[sortDirection]}
                      onClick={direction => {
                        setSortDirection(MessageSetSortDirection[direction])
                        setSortBy(MessageSetSortBy.public)
                      }}>
                      Public
                    </SortableTableCell>
                    <SortableTableCell
                      width={120}
                      active={sortBy === MessageSetSortBy.dynamic}
                      direction={SortDirection[sortDirection]}
                      onClick={direction => {
                        setSortDirection(MessageSetSortDirection[direction])
                        setSortBy(MessageSetSortBy.dynamic)
                      }}>
                      Dynamic
                    </SortableTableCell>
                    <SortableTableCell
                      width={120}
                      active={sortBy === MessageSetSortBy.messagesCount}
                      direction={SortDirection[sortDirection]}
                      onClick={direction => {
                        setSortDirection(MessageSetSortDirection[direction])
                        setSortBy(MessageSetSortBy.messagesCount)
                      }}>
                      Messages
                    </SortableTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.listMessageSets.messageSets.map(messageSet => (
                    <TableRow key={messageSet.id}>
                      <TableCell>
                        <Button
                          onClick={() => {
                            history.push(`message-sets/${messageSet.id}`)
                          }}
                          variant='outlined'
                          size='small'
                          color='primary'>
                          <TruncatedId value={messageSet.id} />
                        </Button>
                      </TableCell>
                      <TableCell>{messageSet.title}</TableCell>
                      <TableCell>
                        {messageSet.isNote === true ? (
                          <Chip label='Note' color='primary' />
                        ) : messageSet.isNote === false ? (
                          <Chip label='Flagship' color='secondary' />
                        ) : (
                          ''
                        )}
                      </TableCell>
                      <TableCell>{messageSet.public ? <Check /> : null}</TableCell>
                      <TableCell>{messageSet.dynamic ? <Check /> : null}</TableCell>
                      <TableCell>{messageSet.messagesCount.toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </InfiniteScroll>
        </>
      )}
    </Box>
  )
}
